/**
 * PHI-SAFE STRUCTURED LOGGER — Kaagazy EMR
 *
 * ⚠️  DPDP Act 2023 / HIPAA: NEVER log PHI — patient names, phone numbers,
 * Aadhaar, diagnoses, medications. Browser console is visible to anyone
 * with DevTools open. Log IDs only (patient_id, prescription_id, user_id).
 *
 * In production only WARN and ERROR are emitted.
 *
 * WARN and ERROR are additionally shipped to the backend via
 * client-error-reporter, so browser-side failures reach CloudWatch instead
 * of dying on the user's device. Console output is unchanged.
 */

import { reportClientLog } from "./client-error-reporter";

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
} as const;

type LogLevel = keyof typeof LOG_LEVELS;
type LogMetadata = Record<string, string | number | boolean | undefined>;

class FrontendLogger {
  private readonly serviceName = "kaagazy-pwa";
  private readonly environment: string =
    process.env.NODE_ENV ?? "development";
  private readonly minLevel: number;

  constructor() {
    this.minLevel =
      this.environment === "production" ? LOG_LEVELS.WARN : LOG_LEVELS.DEBUG;
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVELS[level] >= this.minLevel;
  }

  private format(level: LogLevel, message: string, metadata?: LogMetadata) {
    return JSON.stringify({
      timestamp: new Date().toISOString(),
      level,
      service: this.serviceName,
      message,
      environment: this.environment,
      ...metadata,
    });
  }

  debug(message: string, metadata?: LogMetadata): void {
    if (!this.shouldLog("DEBUG")) return;
    console.log(this.format("DEBUG", message, metadata));
  }

  info(message: string, metadata?: LogMetadata): void {
    if (!this.shouldLog("INFO")) return;
    console.info(this.format("INFO", message, metadata));
  }

  warn(message: string, metadata?: LogMetadata): void {
    if (!this.shouldLog("WARN")) return;
    console.warn(this.format("WARN", message, metadata));
    reportClientLog("WARN", message, undefined, this.requestIdOf(metadata));
  }

  /** Only a correlation id is forwarded; no other metadata leaves the device. */
  private requestIdOf(metadata?: LogMetadata): string | undefined {
    const id = metadata?.request_id;
    return typeof id === "string" ? id : undefined;
  }

  error(message: string, err?: Error, metadata?: LogMetadata): void {
    if (!this.shouldLog("ERROR")) return;
    reportClientLog("ERROR", message, err?.name, this.requestIdOf(metadata));
    console.error(
      this.format("ERROR", message, {
        ...metadata,
        ...(err
          ? { error_name: err.name, error_message: err.message }
          : undefined),
      })
    );
  }
}

export const logger = new FrontendLogger();
